import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.html',
  styleUrls: ['./login.css'],
})
export class LoginComponent implements OnInit {
  form!: FormGroup;
  forgotForm!: FormGroup;
  submitting = false;
  checkingSession = false;
  errorMessage = '';
  showPassword = false;

  showForgotModal = false;
  forgotSubmitted = false;
  forgotSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
  ) {
    this.form = this.fb.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
    });

    this.forgotForm = this.fb.group({
      identifier: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.get('forgot') === '1') {
      this.openForgotModal();
    }

    if (!this.authService.hasValidSession()) {
      return;
    }

    this.checkingSession = true;
    this.authService.validateSession().subscribe({
      next: (valid) => {
        this.checkingSession = false;
        if (valid) {
          const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
          if (returnUrl?.startsWith('/')) {
            void this.router.navigateByUrl(returnUrl, { replaceUrl: true });
          } else {
            void this.router.navigate(['/dashboard'], { replaceUrl: true });
          }
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.checkingSession = false;
        this.cdr.detectChanges();
      },
    });
  }

  openForgotModal(): void {
    this.forgotSubmitted = false;
    this.forgotForm.reset();
    this.showForgotModal = true;
  }

  closeForgotModal(): void {
    this.showForgotModal = false;
    this.forgotSubmitted = false;
    this.forgotSubmitting = false;
    this.forgotForm.reset();
  }

  submitForgotPassword(): void {
    this.forgotForm.markAllAsTouched();
    if (this.forgotForm.invalid) return;

    this.forgotSubmitting = true;
    const identifier = (this.forgotForm.value.identifier as string).trim();

    this.authService.requestPasswordReset(identifier).subscribe({
      next: () => this.onForgotRequestComplete(),
      error: () => this.onForgotRequestComplete(),
    });
  }

  
  private onForgotRequestComplete(): void {
    this.forgotSubmitting = false;
    this.forgotSubmitted = true;
    this.cdr.detectChanges();
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    this.errorMessage = '';

    if (this.form.invalid) return;

    this.submitting = true;
    this.errorMessage = '';
    this.authService.login(this.form.value).subscribe({
      next: () => {
        this.submitting = false;
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
        if (returnUrl?.startsWith('/')) {
          void this.router.navigateByUrl(returnUrl, { replaceUrl: true });
        } else {
          void this.router.navigate(['/dashboard'], { replaceUrl: true });
        }
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        console.error(err);
        this.submitting = false;
        this.errorMessage = err.error?.message ?? 'Sign in failed. Please try again.';
        this.cdr.detectChanges();
      },
    });
  }

  get username() {
    return this.form.get('username');
  }

  get password() {
    return this.form.get('password');
  }

  get forgotIdentifier() {
    return this.forgotForm.get('identifier');
  }
}
